/**
 * L'API rend et reçoit les dates d'une anomalie à l'heure de Paris, sans décalage :
 * `2026-10-23`, `12:00:00`, `2026-10-23T12:00:00` (RG-anomalie-periode-d-affichage). Elles se lisent
 * donc telles quelles, quel que soit le fuseau du poste.
 */
const pad = (n: number) => String(n).padStart(2, '0');

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function parts(value: string): Parts {
  const [date, time = '00:00'] = value.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  return { year, month, day, hour, minute };
}

/** `20/10` */
export function formatDate(value: string): string {
  const p = parts(value);
  return `${pad(p.day)}/${pad(p.month)}`;
}

function formatTime(hour: number, minute: number): string {
  return `${hour} h${minute ? ` ${pad(minute)}` : ''}`;
}

/** `20/10 à 9 h 52`, `22/10 à 12 h` */
export function formatDateTime(value: string): string {
  const p = parts(value);
  return `${formatDate(value)} à ${formatTime(p.hour, p.minute)}`;
}

/** Une date saisie et son heure facultative : `22/10`, `22/10 à 12 h`. */
export function formatBound(date: string, time: string | null): string {
  return time ? formatDateTime(`${date}T${time}`) : formatDate(date);
}

/** Le jour d'une borne de fin : minuit pile clôt la veille. */
export function formatEndDate(value: string): string {
  const p = parts(value);
  return p.hour === 0 && p.minute === 0 ? formatDate(addDays(value, -1)) : formatDate(value);
}

/** Ajoute des jours à une date ou une date-heure, en gardant son heure. */
export function addDays(value: string, days: number): string {
  const [date, time] = value.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day + days));
  const shifted = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  return time === undefined ? shifted : `${shifted}T${time}`;
}

export interface DateAndTime {
  /** `YYYY-MM-DD` */
  date: string;
  /** `HH:mm`, ou `null` sans heure. */
  time: string | null;
}

/**
 * Vrai si le début ne précède pas la fin. Un début sans heure vaut minuit qui ouvre le jour, une fin
 * sans heure minuit qui le clôt, comme dans l'API.
 */
export function isAfterEnd(start: DateAndTime, end: DateAndTime): boolean {
  const from = `${start.date}T${start.time?.slice(0, 5) ?? '00:00'}`;
  const until = end.time ? `${end.date}T${end.time.slice(0, 5)}` : `${addDays(end.date, 1)}T00:00`;
  return from >= until;
}

const parisFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Maintenant, à l'heure de Paris : `2026-10-20T09:52:00`. */
export function parisNow(): string {
  const p: Record<string, string> = {};
  for (const part of parisFormat.formatToParts(new Date())) p[part.type] = part.value;
  return `${p['year']}-${p['month']}-${p['day']}T${p['hour']}:${p['minute']}:${p['second']}`;
}
